import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { notify } from "../lib/notify";

export function About() {
  return (
    <div data-testid="page-about">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">About</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Built for Singapore<br />risk professionals.</h1>
          <p className="text-slate-600 mt-6 max-w-2xl text-lg leading-relaxed">
            RiskPedia Singapore is the localised edition of riskpedia.co.in — translating the same library of 32+ frameworks into MAS, PDPA, CSA and MindForge operating playbooks. Built for CROs, internal auditors, GRC consultants and compliance teams who need fast, accurate, Singapore-specific intelligence.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="overline">Mission</div>
            <h2 className="font-display text-3xl font-semibold mt-3">Match the right risk framework. For Singapore. In under two minutes.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">Risk vocabulary is fragmented across ISO, COSO, NIST, MAS, PDPC and a dozen sectoral overlays. We index the lot, map them to Singapore obligations and surface the right answer fast.</p>
          </div>
          <div>
            <div className="overline">Approach</div>
            <h3 className="font-display text-2xl font-semibold mt-3">Three principles.</h3>
            <ul className="mt-4 space-y-3 text-slate-700">
              <li className="rp-card p-4"><strong className="block font-display">1. Singapore-first.</strong> Every page surfaces MAS / PDPC / CSA implications.</li>
              <li className="rp-card p-4"><strong className="block font-display">2. Educational-only.</strong> We are not legal counsel. Every page links to authoritative sources.</li>
              <li className="rp-card p-4"><strong className="block font-display">3. AI-assisted.</strong> Claude Sonnet 4.5 powers the persona-aware advisor — Kai, Priya, Rajan, Mei.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="section force-dark relative overflow-hidden">
        <div className="bg-grid-dark absolute inset-0 opacity-50" />
        <div className="container-rp relative text-center">
          <div className="overline">Get in touch</div>
          <h2 className="font-display text-4xl font-semibold mt-3">Want to partner, co-publish, or sponsor a webinar?</h2>
          <Link to="/contact" className="btn-accent mt-8 inline-flex">Contact RiskPedia SG <ArrowRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}

export function GenericPage({ title, kicker, body }) {
  return (
    <div data-testid={`page-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">{kicker}</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">{title}</h1>
          <p className="text-slate-600 mt-6 max-w-2xl text-lg leading-relaxed">{body}</p>
        </div>
      </section>
      <section className="section">
        <div className="container-rp">
          <div className="rp-card p-10 text-center">
            <div className="overline">In development</div>
            <h2 className="font-display text-2xl font-semibold mt-3">This destination is being prepared.</h2>
            <p className="text-slate-600 mt-3">Check back soon — or open one of our live destinations below.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/singapore" className="btn-accent">Singapore Hub</Link>
              <Link to="/money-flow" className="btn-outline">Money Flow</Link>
              <Link to="/tools" className="btn-outline">Tools</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function Contact() {
  return (
    <div data-testid="page-contact">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Contact</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Say hello.</h1>
        </div>
      </section>
      <section className="section">
        <div className="container-rp max-w-2xl">
          <form onSubmit={(e) => { e.preventDefault(); e.currentTarget.reset(); notify.success("Thanks — we'll be in touch."); }} className="space-y-4">
            <input required placeholder="Your name" className="w-full border border-slate-200 rounded-sm px-4 py-3 text-sm" data-testid="contact-name" />
            <input required type="email" placeholder="Email" className="w-full border border-slate-200 rounded-sm px-4 py-3 text-sm" data-testid="contact-email" />
            <input placeholder="Organisation (optional)" className="w-full border border-slate-200 rounded-sm px-4 py-3 text-sm" data-testid="contact-org" />
            <textarea required rows={5} placeholder="Your message" className="w-full border border-slate-200 rounded-sm px-4 py-3 text-sm" data-testid="contact-message" />
            <button className="btn-accent" data-testid="contact-submit">Send message <ArrowRight size={14} /></button>
          </form>
        </div>
      </section>
    </div>
  );
}
